/**
 * Responsibility: convert variable real-frame durations into a whole number of
 * fixed-size simulation steps.
 *
 * Why this exists: the flight model must behave identically on a 60 Hz laptop and a
 * 144 Hz monitor. If we fed raw frame deltas into the damping maths, the aircraft would
 * turn at different rates on different hardware. Accumulating time and consuming it in
 * fixed slices removes that dependency entirely.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/**
 * Create a fixed-timestep accumulator.
 *
 * @param {object} options
 * @param {number} options.timestep - Fixed simulation step in seconds.
 * @param {number} options.maxFrameTime - Largest real elapsed time honoured in one
 *   frame. Anything beyond this is discarded rather than simulated, so a long stall
 *   cannot queue hundreds of steps and lock up the page catching up.
 * @returns {{advance: (elapsed: number) => {steps: number, alpha: number}, reset: () => void, getPending: () => number}}
 */
export function createClock({ timestep, maxFrameTime }) {
  if (!(timestep > 0)) {
    throw new Error('clock: timestep must be a positive number of seconds');
  }
  if (!(maxFrameTime > 0)) {
    throw new Error('clock: maxFrameTime must be a positive number of seconds');
  }

  /** Unconsumed simulation time, always less than one timestep after advance(). */
  let accumulator = 0;

  return {
    /**
     * Feed in the real time elapsed since the previous frame.
     *
     * @param {number} elapsed - Seconds since the last frame. Negative or non-finite
     *   values are treated as zero; browsers can hand back odd deltas around tab
     *   switches and clock adjustments, and the simulation should not lurch.
     * @returns {{steps: number, alpha: number}} `steps` is how many fixed updates to
     *   run this frame. `alpha` is the leftover fraction of a step in the range [0, 1),
     *   used to interpolate rendering between simulation states so motion stays smooth
     *   when the display refresh does not divide evenly into the timestep.
     */
    advance(elapsed) {
      const safeElapsed = Number.isFinite(elapsed) && elapsed > 0 ? elapsed : 0;

      // Discard anything past the cap. This intentionally lets the simulation fall
      // behind wall-clock time rather than freeze trying to catch up.
      accumulator += Math.min(safeElapsed, maxFrameTime);

      const steps = Math.floor(accumulator / timestep);
      accumulator -= steps * timestep;

      return { steps, alpha: accumulator / timestep };
    },

    /**
     * Discard pending time. Call this when resuming from pause or loading a save, so
     * the gap while paused is not simulated as a single enormous jump.
     */
    reset() {
      accumulator = 0;
    },

    /** Unconsumed simulation time in seconds. Exposed for tests and debug readouts. */
    getPending() {
      return accumulator;
    },
  };
}
